import { skipToken, useQuery } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { orpc, rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type {
  ListCredentialsInput,
  CredentialSummaryResponse,
  CreateCredentialInput,
  UpdateCredentialInput,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export interface UseCredentialsListOptions {
  organizationId: string | undefined;
  /** Filter by credential type (e.g. "password") */
  type?: ListCredentialsInput['type'];
  limit?: number;
  offset?: number;
}

export function useCredentialsList({
  organizationId,
  type,
  limit = 100,
  offset = 0,
}: UseCredentialsListOptions) {
  const query = useQuery({
    ...orpc.credentials.list.queryOptions({
      input: organizationId
        ? {
            organizationId,
            type,
            limit,
            offset,
          }
        : skipToken,
    }),
    enabled: Boolean(organizationId),
  });

  const credentials: CredentialSummaryResponse[] =
    query.data?.credentials ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    credentials,
    pagination,
  };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateCredential(options?: { onSuccess?: () => void }) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.credentials.list.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: CreateCredentialInput) =>
      rpcClient.credentials.create(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Credential saved successfully',
        color: 'success',
        icon: 'check',
      });
      invalidateRpcQueryFn({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to save credential',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

export function useUpdateCredential(options?: { onSuccess?: () => void }) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.credentials.list.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: UpdateCredentialInput) =>
      rpcClient.credentials.update(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Credential updated successfully',
        color: 'success',
        icon: 'check',
      });
      invalidateRpcQueryFn({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update credential',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

type DeleteInput = Parameters<typeof rpcClient.credentials.delete>[0];

export function useDeleteCredential(options?: { onSuccess?: () => void }) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.credentials.list.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: DeleteInput) => rpcClient.credentials.delete(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Credential deleted successfully',
        color: 'success',
        icon: 'check',
      });
      invalidateRpcQueryFn({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to delete credential',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
